import type { AbstractControl, ValidationErrors } from '@angular/forms';
import type { WeekdayChip, WeeklyRepeatPatch } from './cron-weekly.types';

/** Monday first (Dutch week), Sunday last; `value` is the Date.getDay() number the backend expects. */
export const WEEKDAY_CHIPS: readonly WeekdayChip[] = [
  { value: '1', label: 'Ma', name: 'maandag' },
  { value: '2', label: 'Di', name: 'dinsdag' },
  { value: '3', label: 'Wo', name: 'woensdag' },
  { value: '4', label: 'Do', name: 'donderdag' },
  { value: '5', label: 'Vr', name: 'vrijdag' },
  { value: '6', label: 'Za', name: 'zaterdag' },
  { value: '0', label: 'Zo', name: 'zondag' },
];

/** Normalises a stored `days-of-week` value (strings or numbers, any order) to chip-ordered strings. */
export function chipsFromRepeat(daysOfWeek: readonly (string | number)[] | null | undefined): string[] {
  const stored = new Set((daysOfWeek ?? []).map(String));
  return WEEKDAY_CHIPS.filter(chip => stored.has(chip.value)).map(chip => chip.value);
}

/** Flips one weekday and returns the selection in chip order. */
export function toggleWeekday(selected: readonly (string | number)[] | null | undefined, value: string): string[] {
  const current = new Set(chipsFromRepeat(selected));
  if (current.has(value)) current.delete(value);
  else current.add(value);
  return chipsFromRepeat([...current]);
}

/** The `repeat` keys a weekly series writes for the ticked chips (startDate is the date field's own). */
export function weeklyRepeatFromChips(selected: readonly (string | number)[] | null | undefined): WeeklyRepeatPatch {
  return {
    repeat: 'Wekelijks',
    // A string, like the `weekly` subfield's own default and what updateStartDateRelatedFields writes.
    weekly: '1',
    'days-of-week': chipsFromRepeat(selected),
    'repeat-type-end': 'no-end-date',
  };
}

/**
 * Dutch name of the start date's weekday when it is not among the ticked days, else null. The backend
 * refuses a weekly series that does not run on its first date. Nothing to check without a date or days.
 */
export function missingStartWeekday(
  startDate: string | null | undefined,
  daysOfWeek: readonly (string | number)[] | null | undefined
): string | null {
  const selected = chipsFromRepeat(daysOfWeek);
  if (!startDate || selected.length === 0) return null;
  // A date-only string parses as UTC midnight, which is the day before west of Greenwich.
  const start = new Date(/^\d{4}-\d{2}-\d{2}$/.test(startDate) ? `${startDate}T00:00:00` : startDate);
  if (isNaN(start.getTime())) return null;
  const startDay = start.getDay().toString();
  if (selected.includes(startDay)) return null;
  return WEEKDAY_CHIPS.find(chip => chip.value === startDay)?.name ?? null;
}

/**
 * Group validator for the cron group in weekly style: the start date must fall on a chosen weekday,
 * which the backend demands. The error carries the weekday's name for the message.
 */
export function weeklyStartDayValidator(group: AbstractControl): ValidationErrors | null {
  const day = missingStartWeekday(group.get('startDate')?.value, group.get('days-of-week')?.value);
  return day ? { startDayNotSelected: { day } } : null;
}

/**
 * Group validator for the cron group in weekly style: a series with a start date needs at least one
 * weekday. The full style leaves this to its visible multicheckbox; the chips have no required state.
 */
export function weeklyDaysRequiredValidator(group: AbstractControl): ValidationErrors | null {
  const start = group.get('startDate')?.value;
  return start && chipsFromRepeat(group.get('days-of-week')?.value).length === 0 ? { noWeekdaySelected: true } : null;
}
